<script setup>
import { ref, onMounted } from 'vue'
import { useWorkflowApi } from '../../../composables/useWorkflowApi.js'

const api = useWorkflowApi()

const workflows = ref([])
const showCreateModal = ref(false)
const newWorkflowName = ref('')
const createError = ref(null)
const isCreating = ref(false)

onMounted(async () => {
    try {
        const data = await api.fetchWorkflows()
        workflows.value = Array.isArray(data) ? data : (data?.data ?? [])
    } catch (e) {
        console.error('Failed to load workflows:', e)
    }
})

function openCreateModal() {
    newWorkflowName.value = ''
    createError.value = null
    showCreateModal.value = true
}

function closeCreateModal() {
    showCreateModal.value = false
}

async function createWorkflow() {
    if (!newWorkflowName.value.trim()) {
        createError.value = 'Please enter a workflow name.'
        return
    }
    isCreating.value = true
    createError.value = null
    try {
        const created = await api.createWorkflow({ name: newWorkflowName.value.trim() })
        const id = created?.id ?? created?.data?.id
        if (id) {
            window.location.href = `/bo/communications/workflows/${id}/builder`
        } else {
            workflows.value.unshift(created?.data ?? created)
            closeCreateModal()
        }
    } catch (e) {
        createError.value = e.message
    } finally {
        isCreating.value = false
    }
}

function navigateToBuilder(workflow) {
    window.location.href = `/bo/communications/workflows/${workflow.id}/builder`
}

const statusColors = {
    draft: 'wf-badge--gray',
    active: 'wf-badge--green',
    paused: 'wf-badge--yellow',
    archived: 'wf-badge--gray',
}

function statusBadgeClass(status) {
    return statusColors[status] ?? 'wf-badge--gray'
}

function formatDate(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString(undefined, { dateStyle: 'medium' })
}
</script>

<template>
    <div class="wf-index">
        <!-- Header -->
        <div class="wf-index__header">
            <div>
                <h1 class="wf-index__title">Workflows</h1>
                <p class="wf-index__subtitle">Automate contact journeys with visual workflows.</p>
            </div>
            <button class="wf-index__create-btn" @click="openCreateModal">+ New Workflow</button>
        </div>

        <!-- Loading -->
        <div v-if="api.loading.value" class="wf-index__loading">Loading workflows...</div>

        <!-- Empty state -->
        <div v-else-if="workflows.length === 0" class="wf-index__empty">
            <p>No workflows yet.</p>
            <button class="wf-index__create-btn" @click="openCreateModal">Create your first workflow</button>
        </div>

        <!-- Table -->
        <div v-else class="wf-index__table-wrap">
            <table class="wf-index__table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Last Updated</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="wf in workflows"
                        :key="wf.id"
                        class="wf-index__row"
                        @click="navigateToBuilder(wf)"
                    >
                        <td class="wf-index__name">{{ wf.name }}</td>
                        <td>
                            <span class="wf-badge" :class="statusBadgeClass(wf.status)">
                                {{ wf.status ?? 'draft' }}
                            </span>
                        </td>
                        <td class="wf-index__date">{{ formatDate(wf.updated_at) }}</td>
                        <td class="wf-index__action">
                            <button class="wf-index__open-btn" @click.stop="navigateToBuilder(wf)">Open</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Create Modal -->
        <div v-if="showCreateModal" class="wf-modal-overlay" @click.self="closeCreateModal">
            <div class="wf-modal">
                <h2 class="wf-modal__title">New Workflow</h2>
                <label class="wf-modal__label">Workflow Name</label>
                <input
                    v-model="newWorkflowName"
                    class="wf-modal__input"
                    type="text"
                    placeholder="e.g. Welcome Series"
                    autofocus
                    @keydown.enter="createWorkflow"
                    @keydown.escape="closeCreateModal"
                />
                <p v-if="createError" class="wf-modal__error">{{ createError }}</p>
                <div class="wf-modal__actions">
                    <button class="wf-modal__btn wf-modal__btn--cancel" @click="closeCreateModal">Cancel</button>
                    <button
                        class="wf-modal__btn wf-modal__btn--create"
                        :disabled="isCreating"
                        @click="createWorkflow"
                    >
                        {{ isCreating ? 'Creating...' : 'Create' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.wf-index {
    max-width: 900px;
    margin: 0 auto;
    padding: 32px 24px;
}
.wf-index__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 28px;
}
.wf-index__title {
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 4px;
}
.wf-index__subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}
.wf-index__create-btn {
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 9px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
}
.wf-index__create-btn:hover {
    background: #2563eb;
}
.wf-index__loading {
    color: #6b7280;
    font-size: 14px;
    padding: 24px 0;
    text-align: center;
}
.wf-index__empty {
    text-align: center;
    padding: 60px 0;
    color: #6b7280;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}
.wf-index__table-wrap {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
}
.wf-index__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.wf-index__table thead {
    background: #f9fafb;
}
.wf-index__table th {
    text-align: left;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e5e7eb;
}
.wf-index__row {
    cursor: pointer;
    transition: background 0.1s;
}
.wf-index__row:hover {
    background: #f0f9ff;
}
.wf-index__row td {
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
}
.wf-index__row:last-child td {
    border-bottom: none;
}
.wf-index__name {
    font-weight: 600;
    color: #111827;
}
.wf-index__date {
    color: #6b7280;
    font-size: 13px;
}
.wf-index__action {
    text-align: right;
}
.wf-index__open-btn {
    background: none;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    color: #374151;
    transition: background 0.1s;
}
.wf-index__open-btn:hover {
    background: #f3f4f6;
}

/* Badges */
.wf-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
}
.wf-badge--green {
    background: #dcfce7;
    color: #16a34a;
}
.wf-badge--yellow {
    background: #fef9c3;
    color: #a16207;
}
.wf-badge--gray {
    background: #f3f4f6;
    color: #6b7280;
}

/* Modal */
.wf-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.wf-modal {
    background: white;
    border-radius: 10px;
    padding: 28px 28px 24px;
    min-width: 380px;
    max-width: 480px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}
.wf-modal__title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 16px;
}
.wf-modal__label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.wf-modal__input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 9px 12px;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
}
.wf-modal__input:focus {
    border-color: #3b82f6;
}
.wf-modal__error {
    color: #ef4444;
    font-size: 12px;
    margin: 6px 0 0;
}
.wf-modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
}
.wf-modal__btn {
    padding: 8px 18px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: opacity 0.15s;
}
.wf-modal__btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.wf-modal__btn--cancel {
    background: #f3f4f6;
    color: #374151;
}
.wf-modal__btn--cancel:hover {
    background: #e5e7eb;
}
.wf-modal__btn--create {
    background: #3b82f6;
    color: white;
}
.wf-modal__btn--create:hover:not(:disabled) {
    background: #2563eb;
}
</style>
